import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { User, LogOut, ChevronRight, BriefcaseBusiness, MessageSquareWarning, Ticket } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/app/navigation/use-navigation';
import { supabase } from '@/lib/supabase';
import { useFeedbackWidget } from '@/features/feedback/feedback-widget-context';
import Empresa from '@/features/admin/components/empresa/Empresa';

export default function UserMenu({ isExpanded = true }: { isExpanded?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [showEmpresa, setShowEmpresa] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const { user, pessoa, signOut, hasPermission } = useAuth();
  const { actions: navActions } = useNavigation();

  // Feedback widget bridge (headless): unseenCount drives the avatar resolve
  // badge + its accessible label; handle opens the report / resolved view from
  // the menu. Null before the widget mounts or outside the provider — the
  // entries stay inert (disabled) until the widget is live.
  const feedback = useFeedbackWidget();
  const feedbackHandle = feedback?.handle ?? null;
  const resolveUnseen = feedback?.unseenCount ?? 0;

  // Permissão para ver "Minha Empresa"
  const canViewEmpresa = hasPermission('EMPRESA');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Carregar avatar do usuário logado
  const loadAvatar = useCallback(async () => {
    if (!pessoa?.id) {
      setAvatarUrl(null);
      return;
    }
    try {
      const { data } = await supabase
        .from('entidade_file')
        .select('file:file_id(nome_storage)')
        .eq('entidade', 'pessoa')
        .eq('entidade_id', pessoa.id)
        .eq('papel', 'avatar')
        .is('deleted_at', null)
        .maybeSingle();

      if (data?.file?.nome_storage) {
        const { data: urlData } = await supabase.storage
          .from('documento')
          .createSignedUrl(data.file.nome_storage, 60 * 60 * 24 * 365);
        setAvatarUrl(urlData?.signedUrl || null);
      } else {
        setAvatarUrl(null);
      }
    } catch (err) {
      setAvatarUrl(null);
    }
  }, [pessoa?.id]);

  useEffect(() => {
    loadAvatar();
  }, [loadAvatar]);

  // Calcular posição do dropdown
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.right + 12
      });
    }
  }, [isOpen]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      setIsOpen(false);
      await signOut();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      alert('Erro ao fazer logout: ' + (error as Error).message);
    }
  };

  const userName = pessoa?.apelido || user?.email?.split('@')[0] || pessoa?.nome || 'Usuário';
  const userEmail = user?.email || '';
  const userInitials = userName
    .split(' ')
    .slice(0, 2)
    .map(name => name[0])
    .join('')
    .toUpperCase();

  return (
    <div className="user-menu" style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        className="user-menu__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={
          resolveUnseen > 0
            ? `Menu do usuário — ${userName} — ${resolveUnseen} ${resolveUnseen > 1 ? 'tickets resolvidos' : 'ticket resolvido'}`
            : `Menu do usuário — ${userName}`
        }
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'flex-start' : 'center',
          gap: isExpanded ? '12px' : '4px',
          flexDirection: isExpanded ? 'row' : 'column',
          padding: isExpanded ? '8px 12px' : '8px',
          background: isOpen
            ? 'color-mix(in srgb, var(--sidebar-fg) 15%, transparent)'
            : 'transparent',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          color: 'var(--sidebar-fg)'
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'color-mix(in srgb, var(--sidebar-fg) 10%, transparent)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
      >
        {/* Avatar — wrapped so the resolve badge can sit in the top-right corner
            (the .user-avatar itself has overflow:hidden, which would clip it). */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
        <div
          className="user-avatar"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: avatarUrl
              ? 'transparent'
              : 'color-mix(in srgb, var(--sidebar-fg) 20%, transparent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--sidebar-fg)',
            fontSize: '13px',
            fontWeight: '600',
            flexShrink: 0,
            border: '2px solid color-mix(in srgb, var(--sidebar-fg) 30%, transparent)',
            overflow: 'hidden',
            filter: 'var(--sidebar-logo-shadow)'
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={userName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : (
            userInitials
          )}
        </div>
        {/* Resolve badge: notification-red dot when the user has resolved-but-unseen
            tickets. Decorative (aria-hidden) — the count is on the trigger's
            aria-label. Halo ring keeps it legible over any avatar photo/theme. */}
        {resolveUnseen > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-1px',
              right: '-1px',
              width: '11px',
              height: '11px',
              borderRadius: '50%',
              background: 'var(--color-danger, #ef4444)',
              boxShadow: '0 0 0 1.5px rgba(255, 255, 255, 0.85)',
              pointerEvents: 'none'
            }}
          />
        )}
        </div>

        {/* Info do usuário - só quando expandido */}
        {isExpanded && (
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <div style={{
              fontWeight: '600',
              fontSize: '14px',
              lineHeight: '1.3',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {userName.length > 18 ? userName.substring(0, 18) + '...' : userName}
            </div>
            {userEmail && (
              <div style={{
                fontSize: '12px',
                opacity: 0.7,
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {userEmail.length > 22 ? userEmail.substring(0, 22) + '...' : userEmail}
              </div>
            )}
          </div>
        )}

        {/* Chevron - aponta para direita (dropdown abre à direita) */}
        <ChevronRight
          size={isExpanded ? 16 : 14}
          style={{
            transition: 'transform 0.2s ease',
            flexShrink: 0,
            opacity: 0.7
          }}
        />
      </button>

      {/* Dropdown - renderizado via Portal fora da hierarquia do SideNav */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="user-menu__dropdown"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            transform: 'translateY(-100%)',
            background: 'rgba(255, 255, 255, 0.50)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)',
            width: '240px',
            zIndex: 10000,
            overflow: 'hidden'
          }}
        >
          {/* Header do dropdown com info do usuário */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            <div style={{
              fontWeight: '600',
              fontSize: '14px',
              color: 'var(--text)',
              marginBottom: '2px'
            }}>
              {userName}
            </div>
            <div style={{
              fontSize: '12px',
              color: 'var(--muted)'
            }}>
              {userEmail}
            </div>
          </div>

          <div className="user-menu__options">
            {/* Meu Perfil — session drawer (entity is the logged-in user, no id in URL) */}
            <button
              className="user-menu__option"
              onClick={() => {
                setIsOpen(false);
                navActions.pushOverlayDrawer('meu_perfil');
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                color: 'var(--text)',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--panel)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <User size={16} />
              Meu Perfil
            </button>

            {/* Minha Empresa - só mostra se tem permissão */}
            {canViewEmpresa && (
              <button
                className="user-menu__option"
                onClick={() => {
                  setIsOpen(false);
                  setShowEmpresa(true);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  color: 'var(--text)',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--panel)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <BriefcaseBusiness size={16} />
                Minha Empresa
              </button>
            )}

            {/* Divider */}
            <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />

            {/* Reportar problema — abre o widget de feedback (TicketHub) na view
                de report. Inerte (disabled) até o widget montar e publicar o handle. */}
            <button
              className="user-menu__option"
              disabled={!feedbackHandle}
              onClick={() => {
                setIsOpen(false);
                feedbackHandle?.open('report');
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: feedbackHandle ? 'pointer' : 'not-allowed',
                opacity: feedbackHandle ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                color: 'var(--text)',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => { if (feedbackHandle) e.currentTarget.style.background = 'var(--panel)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <MessageSquareWarning size={16} />
              Reportar problema
            </button>

            {/* Meus tickets — abre o widget na view de tickets; mostra o contador
                de resolvidos-não-vistos quando > 0 (limpa ao abrir, via bridge). */}
            <button
              className="user-menu__option"
              disabled={!feedbackHandle}
              aria-label={
                resolveUnseen > 0
                  ? `Meus tickets — ${resolveUnseen} ${resolveUnseen > 1 ? 'resolvidos' : 'resolvido'}`
                  : undefined
              }
              onClick={() => {
                setIsOpen(false);
                feedbackHandle?.open('tickets');
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: feedbackHandle ? 'pointer' : 'not-allowed',
                opacity: feedbackHandle ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                fontSize: '14px',
                color: 'var(--text)',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => { if (feedbackHandle) e.currentTarget.style.background = 'var(--panel)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Ticket size={16} />
                Meus tickets
              </span>
              {resolveUnseen > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    minWidth: '18px',
                    height: '18px',
                    padding: '0 5px',
                    borderRadius: '9px',
                    // Darker danger token (not --color-danger #ef4444) so white text
                    // clears WCAG AA: #b91c1c ≈ 6.5:1 vs #ef4444's ~3.8:1 at 11px.
                    background: 'var(--color-danger-dark, #b91c1c)',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1
                  }}
                >
                  {resolveUnseen > 99 ? '99+' : resolveUnseen}
                </span>
              )}
            </button>

            {/* Divider */}
            <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />

            {/* Versão */}
            <div style={{
              padding: '8px 16px',
              fontSize: '12px',
              color: 'var(--muted)'
            }}>
              Versão {__APP_VERSION__ || 'dev'}
            </div>

            {/* Divider */}
            <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />

            {/* Sair */}
            <button
              className="user-menu__option"
              onClick={handleLogout}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                color: 'var(--danger)',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--danger-light, #fef2f2)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <LogOut size={16} />
              Sair
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Drawer Minha Empresa */}
      <Empresa
        isOpen={showEmpresa}
        onClose={() => setShowEmpresa(false)}
      />

      {/* Meu Perfil drawer is rendered centrally by OverlayDrawerHost (session
          drawer kind: `?drawer=meu_perfil`, no id). */}
    </div>
  );
}
